// Lightweight ICS (iCalendar) generator for browser-side export.
// Used by the Schedule & Remind "Sync to Google" prompt so guests can pocket
// their calendar without an account.

const pad = (n) => String(n).padStart(2, '0');

// ICS uses "floating" or "Zulu" timestamps. We emit Zulu (UTC) for portability.
function toIcsUtc(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return (
    d.getUTCFullYear().toString() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    'T' +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    'Z'
  );
}

// Per RFC 5545, escape commas, semicolons, backslashes, and newlines in TEXT.
function escapeText(s) {
  if (!s) return '';
  return String(s)
    .replace(/\\/g, '\\\\')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;')
    .replace(/\r?\n/g, '\\n');
}

function foldLine(line) {
  // ICS lines should be folded at 75 octets. Quick split good enough for our payloads.
  if (line.length <= 75) return line;
  const out = [];
  for (let i = 0; i < line.length; i += 73) {
    out.push((i === 0 ? '' : ' ') + line.slice(i, i + 73));
  }
  return out.join('\r\n');
}

export function buildIcs(events, { calName = 'BIL · Schedule & Remind' } = {}) {
  const now = toIcsUtc(new Date().toISOString());
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//BIL//Schedule & Remind//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    foldLine(`X-WR-CALNAME:${escapeText(calName)}`),
  ];

  events.forEach((ev) => {
    if (!ev || !ev.start_at) return;
    const startIso = ev.start_at;
    const duration = Number(ev.duration_minutes) || 30;
    const endIso = new Date(new Date(startIso).getTime() + duration * 60 * 1000).toISOString();
    const summary = ev.customer_name
      ? `${ev.title || 'Appointment'} — ${ev.customer_name}`
      : (ev.title || 'Appointment');
    const descParts = [];
    if (ev.customer_email) descParts.push(`Email: ${ev.customer_email}`);
    if (ev.customer_phone) descParts.push(`Phone: ${ev.customer_phone}`);
    if (ev.meeting_link)   descParts.push(`Join: ${ev.meeting_link}`);
    if (ev.notes) descParts.push(ev.notes);
    const description = descParts.join('\n');

    lines.push('BEGIN:VEVENT');
    lines.push(`UID:${ev.id || `bil-${Math.random().toString(36).slice(2)}`}@ubil.net`);
    lines.push(`DTSTAMP:${now}`);
    lines.push(`DTSTART:${toIcsUtc(startIso)}`);
    lines.push(`DTEND:${toIcsUtc(endIso)}`);
    lines.push(foldLine(`SUMMARY:${escapeText(summary)}`));
    if (description) lines.push(foldLine(`DESCRIPTION:${escapeText(description)}`));
    if (ev.location) lines.push(foldLine(`LOCATION:${escapeText(ev.location)}`));
    if (ev.meeting_link) lines.push(foldLine(`URL:${escapeText(ev.meeting_link)}`));
    if (Array.isArray(ev.attendees)) {
      ev.attendees.forEach((email) => {
        if (!email) return;
        lines.push(foldLine(`ATTENDEE;RSVP=TRUE;CN=${escapeText(email)}:mailto:${email}`));
      });
    }
    if (ev.status === 'cancelled') lines.push('STATUS:CANCELLED');
    lines.push('END:VEVENT');
  });

  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
}

export function downloadIcs(events, filename = 'bil-schedule.ics') {
  const ics = buildIcs(events);
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
